import { useCallback, useContext } from 'react';
import af from 'date-fns/locale/af';
import ar from 'date-fns/locale/ar';
import ca from 'date-fns/locale/ca';
import de from 'date-fns/locale/de';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import lt from 'date-fns/locale/lt';
import pl from 'date-fns/locale/pl';
import pt from 'date-fns/locale/pt';
import ro from 'date-fns/locale/ro';
import ru from 'date-fns/locale/ru';
import sk from 'date-fns/locale/sk';
import uk from 'date-fns/locale/uk';
import vi from 'date-fns/locale/vi';
import zh from 'date-fns/locale/zh-CN';
import { registerLocale } from 'react-datepicker';

import { usePostMessageListener } from '@hooks/usePostMessageListener';
import { InitConfigPayload, PostMessageType } from '@spazious/search-sdk-types';

import LanguageContext from './LanguageContext.js';

registerLocale('de', de);
registerLocale('es', es);
registerLocale('fr', fr);
registerLocale('it', it);
registerLocale('pt', pt);
registerLocale('ro', ro);
registerLocale('ru', ru);
registerLocale('sk', sk);
registerLocale('zh', zh);
registerLocale('ja', ja);
registerLocale('ar', ar);
registerLocale('pl', pl);
registerLocale('ca', ca);
registerLocale('af', af);
registerLocale('ko', ko);
registerLocale('lt', lt);
registerLocale('uk', uk);
registerLocale('vi', vi);

export enum AvailableLanguages {
  en = 'en',
  es = 'es',
  de = 'de',
  fr = 'fr',
  it = 'it',
  pt = 'pt',
  ro = 'ro',
  zh = 'zh',
  ja = 'ja',
  ar = 'ar',
  ru = 'ru',
  pl = 'pl',
  ca = 'ca',
  af = 'af',
  ko = 'ko',
  lt = 'lt',
  uk = 'uk',
  vi = 'vi',
}

/**
 * Hook to get and set current language/locale.
 * Locale now comes from SDK INIT_CONFIG message (not URL).
 */
export const useLanguage = () => {
  const {
    locale: stateLocale,
    setLocale: setStateLocale = () => {
      console.warn('useLanguage: No provider detected');
    },
  } = useContext(LanguageContext) ?? {};

  usePostMessageListener<InitConfigPayload>(
    PostMessageType.INIT_CONFIG,
    useCallback(
      (payload: any) => {
        if (typeof payload?.locale === 'string') {
          setStateLocale(payload.locale);
        }
      },
      [setStateLocale]
    )
  );
  const setLocale = useCallback(
    (locale: string | null) => {
      setStateLocale(locale);
    },
    [setStateLocale]
  );

  return {
    locale: stateLocale ?? 'en',
    languages: Object.values(AvailableLanguages),
    setLocale,
  };
};

export default useLanguage;
